import axios from "axios";
import request from "utils/request";
import PercentMap from "assets/json/percent.json";

const DEFAULT_BEFORE_DAY = 7;

type TotalType = {
  mom: number;
  yoy: number;
  imp: number;
  count: number;
};

enum DataKey {
  "advertiser" = "advertiser",
  "play" = "play",
  "play_classification_L2" = "play_classification_L2",
  "agency" = "agency",
  "media" = "media",
  "spot_type" = "spot_type",
  "display_type" = "display_type",
  "region_province" = "region_province",
  "campaign_name" = "campaign_name",
  "region_city" = "region_city",
  "industry_L1" = "industry_L1",
  "plt" = "plt",
  "brand" = "brand",
}

type DimensionType = { [X in DataKey]: { name: string; value: number }[] };

type DataType = {
  total: {
    yesterday: TotalType;
    today: TotalType;
  };
  dimension: DimensionType;
  percent: number[];
};

export const defaultData: DataType = {
  total: {
    yesterday: {
      mom: 0,
      yoy: 0,
      imp: 0,
      count: 0,
    },
    today: {
      mom: 0,
      yoy: 0,
      imp: 0,
      count: 0,
    },
  },
  dimension: Object.fromEntries(
    Object.keys(DataKey).map((k) => [k, [] as any[]])
  ) as DimensionType,
  percent: PercentMap["全国"],
};

function getDate(beforeDay: number) {
  const date = new Date().getTime();
  const d = new Date(date - beforeDay * 24 * 60 * 60 * 1000);
  return [d.getFullYear(), d.getMonth() + 1, d.getDate()]
    .map((s) => `${s}`.padStart(2, "0"))
    .join("-");
}

export function GetTotalSIMP(name: string, day: number = DEFAULT_BEFORE_DAY) {
  return request({
    url: `/selectTotalDataByDay?date=${getDate(day)}&region=${name}`,
    method: "get",
  });
}

export function GetAllDimension(
  name: string,
  day: number = DEFAULT_BEFORE_DAY
) {
  return request({
    url: `/selectDataByDay?date=${getDate(day)}&region=${name}`,
    method: "get",
  });
}

let RetryTime = 0;

export function getAll(name: string, beforeDay: number = DEFAULT_BEFORE_DAY) {
  return axios
    .all([
      GetTotalSIMP(name, beforeDay + 1),
      GetTotalSIMP(name, beforeDay),
      GetAllDimension(name, beforeDay),
    ])
    .then(
      axios.spread(
        (yesterday: any, today: any, dimension: any): DataType => {
          if (JSON.stringify(yesterday) === "{}") {
            throw new Error(
              `无法获取${beforeDay + 1}天前（${getDate(
                beforeDay + 1
              )}）的Total数据！`
            );
          }

          if (JSON.stringify(today) === "{}") {
            throw new Error(
              `无法获取${beforeDay}天前（${getDate(beforeDay)}）的Total数据！`
            );
          }

          const yesterdayData = yesterday?.calculate?.[0] ?? {};
          const todayData = today?.calculate?.[0] ?? {};

          RetryTime = 0;

          return {
            total: {
              yesterday: {
                ...yesterdayData,
                mom: Math.round(yesterdayData.mom * 100),
                yoy: Math.round(yesterdayData.yoy * 100),
              },
              today: {
                ...todayData,
                mom: Math.round(todayData.mom * 100),
                yoy: Math.round(todayData.yoy * 100),
              },
            },
            dimension: Object.fromEntries(
              // 将每种dimension
              Object.keys(dimension)
                // 的每一项数据的key:
                .map((k) => [
                  k,
                  dimension[k].map((dk: any) => ({
                    // 类别变为name, timp变为value
                    name: dk[k],
                    value: dk.timp,
                  })),
                ])
            ) as DimensionType,
            percent: PercentMap[name as keyof typeof PercentMap],
          };
        }
      )
    )
    .catch(
      (e): Promise<DataType> => {
        console.error(e);
        // 尝试重试三次，每次再早一天
        if (RetryTime < 3) {
          RetryTime += 1;
          return getAll(name, beforeDay + 1);
        }
        RetryTime = 0;
        return JSON.parse(JSON.stringify(defaultData));
      }
    )
    .then((value) => Promise.resolve(value));
}

export function getPlaylist() {
  // 避免浏览器缓存
  const t = new Date().getTime();
  return request({
    baseURL: "",
    url: "/video/playlist.json",
    method: "get",
    params: {
      t,
    },
  });
}

export function getGeoJson(adcode: string) {
  return request({
    baseURL: "",
    url: `geo/${adcode}.geoJson`,
    method: "get",
  });
}
